import {
  ComboBox,
  DefaultButton,
  Dropdown,
  IComboBox,
  IComboBoxOption,
  IDropdownOption,
  Link,
  MessageBar,
  MessageBarType,
  Panel,
  PanelType,
  PrimaryButton,
  Spinner,
  SpinnerSize,
  Stack,
  Text,
} from "@fluentui/react";
import React from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../redux";
import { LanguageContext } from "../../../../utilities/LocalizationModule";
import { logger } from "../../../../utilities/logger";
import { IHixCompany, IHixUploadItem, IHixUploadType } from "../../interfaces/IHix";
import { apiHixGetCompanies, apiHixGetUploadTypes, apiHixUploadTaskFiles } from "../../services/ApiService";
import { parseSiteTitle, searchNameWords, splitNameWords } from "../helpers/hixClientMatch";

// HIX accepts pdf/xbrl/xbri/html/xml directly and auto-converts common office formats to PDF.
// Anything outside this set is flagged and excluded from the upload.
const HIX_SUPPORTED_EXTENSIONS = new Set<string>([
  "PDF", "XBRL", "XBRI", "HTML", "HTM", "XML",
  "DOC", "DOCX", "DOCM", "DOT", "DOTX", "DOTM",
  "XLS", "XLSX", "XLSM", "XLSB",
  "PPT", "PPTX", "PPTM",
  "RTF", "ODT", "ODS", "ODP",
]);

const MAX_HIX_FILES = 6;

// Synthetic first entry of the upload-type dropdown, mapping to HIX's generic
// "Upload bestand(en)" draft task: selecting it omits upload_type_id from the request.
// Real HIX upload-type ids are positive, so 0 cannot collide.
const GENERIC_UPLOAD_TYPE_KEY = 0;

export interface IHixUploadFile {
  name: string;
  fileType?: string;
  isFolder?: boolean;
}

export interface IHixUploadPanelProps {
  isOpen: boolean;
  onDismiss: () => void;
  siteDisplayName?: string;
  files: IHixUploadFile[];
  getUploadItems: () => Promise<IHixUploadItem[]>;
  getAccessToken: () => Promise<string>;
}

const getExtension = (name?: string): string => {
  if (!name) return "";
  const dot = name.lastIndexOf(".");
  return dot >= 0 ? name.substring(dot + 1).toUpperCase() : "";
};

const isSupported = (file: IHixUploadFile): boolean => {
  if (file.isFolder) return false;
  const ext = (file.fileType || getExtension(file.name) || "").toUpperCase();
  return HIX_SUPPORTED_EXTENSIONS.has(ext);
};

const isSupportedName = (name: string): boolean => HIX_SUPPORTED_EXTENSIONS.has(getExtension(name));

const toCompanyOption = (c: IHixCompany): IComboBoxOption => ({ key: c.id, text: c.name });

// Open the created task, reporting whether anything actually opened. BrowserUtils'
// OpenInBrowser returns void, but the caller needs the outcome: window.open from an
// async success callback is fair game for popup blockers, and a blocked open must fall
// back to a clickable link. The Office path (system browser) is never popup-blocked.
const openTaskInBrowser = (url: string): boolean => {
  try {
    if (typeof Office !== "undefined" && Office.context?.requirements?.isSetSupported("OpenBrowserWindowApi")) {
      Office.context.ui.openBrowserWindow(url);
      return true;
    }
  } catch (error) {
    logger(error, "HixUploadPanel.tsx (openTaskInBrowser)");
  }
  return !!window.open(url, "_blank");
};

export const HixUploadPanel = ({
  isOpen,
  onDismiss,
  siteDisplayName,
  files,
  getUploadItems,
  getAccessToken,
}: IHixUploadPanelProps) => {
  const languageStrings = React.useContext(LanguageContext);

  // tenantId / accountId are ambient session identity (same in every host), so they are read
  // from the store here rather than threaded through every caller as props.
  const tenantId = useSelector((state: RootState) => state.tenantInfo?.tenantId);
  const accountId = useSelector((state: RootState) => state.user?.account?.accountId);

  const [companyOptions, setCompanyOptions] = React.useState<IComboBoxOption[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = React.useState<number | undefined>(undefined);
  const [isSearching, setIsSearching] = React.useState(false);

  const [uploadTypeOptions, setUploadTypeOptions] = React.useState<IDropdownOption[]>([]);
  // Defaults to the generic entry; a firm-defined HIX upload type is an explicit choice.
  const [selectedUploadTypeId, setSelectedUploadTypeId] = React.useState<number>(GENERIC_UPLOAD_TYPE_KEY);

  const uploadTypeDropdownOptions = React.useMemo<IDropdownOption[]>(
    () => [{ key: GENERIC_UPLOAD_TYPE_KEY, text: languageStrings.HixUploadTypeGeneric }, ...uploadTypeOptions],
    [uploadTypeOptions, languageStrings]
  );

  const [isUploading, setIsUploading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string>("");
  const [successMessage, setSuccessMessage] = React.useState<string>("");
  // Non-empty when the created task could not be auto-opened (popup blocked): the
  // persistent success bar then carries this link. uploadCompleted keeps a second
  // submit off the table while the panel stays open after a success.
  const [uploadedTaskUrl, setUploadedTaskUrl] = React.useState<string>("");
  const [uploadCompleted, setUploadCompleted] = React.useState(false);

  const searchDebounce = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // focus(true) opens the suggestion menu — neither freeform typing nor programmatic
  // option seeding opens it on its own, leaving results invisible behind the caret.
  const comboBoxRef = React.useRef<IComboBox>(null);

  const supportedFiles = React.useMemo(() => files.filter(isSupported), [files]);
  const unsupportedFiles = React.useMemo(() => files.filter(f => !f.isFolder && !isSupported(f)), [files]);
  const tooManyFiles = supportedFiles.length > MAX_HIX_FILES;
  const noSupportedFiles = supportedFiles.length === 0;

  // Merge newly fetched companies into the options without dropping the selected one.
  const mergeCompanyOptions = React.useCallback((companies: IHixCompany[]) => {
    setCompanyOptions(prev => {
      const map = new Map<number | string, IComboBoxOption>();
      prev.forEach(o => map.set(o.key, o));
      companies.forEach(c => map.set(c.id, toCompanyOption(c)));
      return Array.from(map.values());
    });
  }, []);

  // Initial load: upload types + client pre-selection from the site title.
  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const uploadTypes: IHixUploadType[] = await apiHixGetUploadTypes(tenantId, accountId);
        if (!cancelled && Array.isArray(uploadTypes)) {
          setUploadTypeOptions(uploadTypes.map(t => ({ key: t.id, text: t.name })));
        }
      } catch (error) {
        logger(error, "HixUploadPanel.tsx (load upload types)");
      }

      // Pre-select a client from the site title: leading code -> internal_client_id, else name.
      try {
        const parsed = parseSiteTitle(siteDisplayName);
        let preselected: IHixCompany | undefined;
        let seedCompanies: IHixCompany[] = [];

        if (!cancelled && (parsed.code || parsed.name)) {
          setIsSearching(true);
        }

        if (parsed.code) {
          const byCode = await apiHixGetCompanies(tenantId, accountId, undefined, parsed.code);
          const matches = byCode?.data ?? [];
          seedCompanies = matches;
          const exact = matches.filter(m => m.internal_client_id?.trim().toLowerCase() === parsed.code!.trim().toLowerCase());
          if (exact.length === 1) {
            preselected = exact[0];
          }
        }

        if (!preselected && parsed.name) {
          const fullName = parsed.name.trim().toLowerCase();
          const byName = await apiHixGetCompanies(tenantId, accountId, parsed.name);
          const matches = byName?.data ?? [];
          seedCompanies = seedCompanies.concat(matches);
          const exact = matches.filter(m => m.name?.trim().toLowerCase() === fullName);
          if (exact.length === 1) {
            preselected = exact[0];
          }

          // The full name found nothing — search each of its words in parallel and seed
          // the union as suggestions. A word equal to the full name would just repeat
          // the failed query, so it is skipped (which also covers single-word names).
          if (matches.length === 0) {
            const words = splitNameWords(parsed.name).filter(w => w.toLowerCase() !== fullName);
            if (words.length > 0) {
              const wordMatches = await searchNameWords(words, word => apiHixGetCompanies(tenantId, accountId, word));
              seedCompanies = seedCompanies.concat(wordMatches);
              const exactWord = wordMatches.filter(m => m.name?.trim().toLowerCase() === fullName);
              if (exactWord.length === 1) {
                preselected = exactWord[0];
              } else if (wordMatches.length === 1) {
                // The whole fallback surfaced exactly one company — unambiguous enough to
                // select even though its name is not the full site-title name
                // (site "Docubird Demo" -> sole match "Docubird").
                preselected = wordMatches[0];
              }
            }
          }
        }

        if (!cancelled) {
          if (seedCompanies.length > 0) {
            mergeCompanyOptions(seedCompanies);
          }
          if (preselected) {
            setSelectedCompanyId(preselected.id);
          } else if (seedCompanies.length > 0) {
            // Nothing was unambiguous enough to select — open the menu so the seeded
            // suggestions are visible instead of sitting behind the caret.
            comboBoxRef.current?.focus(true);
          }
        }
      } catch (error) {
        logger(error, "HixUploadPanel.tsx (pre-select client)");
      } finally {
        if (!cancelled) {
          setIsSearching(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, tenantId, accountId, siteDisplayName, mergeCompanyOptions]);

  const searchCompanies = React.useCallback((query: string) => {
    if (searchDebounce.current) {
      clearTimeout(searchDebounce.current);
    }
    searchDebounce.current = setTimeout(async () => {
      if (!query || !query.trim()) {
        return;
      }
      setIsSearching(true);
      try {
        const result = await apiHixGetCompanies(tenantId, accountId, query.trim());
        const found = result?.data ?? [];
        mergeCompanyOptions(found);
        if (found.length > 0) {
          // Freeform typing does not open the menu by itself — open it so the results
          // show as suggestions.
          comboBoxRef.current?.focus(true);
        }
      } catch (error) {
        logger(error, "HixUploadPanel.tsx (search companies)");
      } finally {
        setIsSearching(false);
      }
    }, 400);
  }, [tenantId, accountId, mergeCompanyOptions]);

  const onCompanyInputChange = (value: string) => {
    searchCompanies(value);
  };

  const onCompanyChange = (_event: React.FormEvent<IComboBox>, option?: IComboBoxOption) => {
    if (option) {
      setSelectedCompanyId(option.key as number);
    }
  };

  const onUploadTypeChange = (_event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
    setSelectedUploadTypeId(option ? (option.key as number) : GENERIC_UPLOAD_TYPE_KEY);
  };

  const canUpload = !isUploading
    && !uploadCompleted
    && !!selectedCompanyId
    && !noSupportedFiles
    && !tooManyFiles;

  const upload = async () => {
    if (!canUpload) {
      return;
    }
    setErrorMessage("");
    setSuccessMessage("");
    setIsUploading(true);

    try {
      const accessToken = await getAccessToken();
      const allItems = await getUploadItems();
      const items = (allItems ?? []).filter(i => isSupportedName(i.name)).slice(0, MAX_HIX_FILES);

      if (items.length === 0) {
        setErrorMessage(languageStrings.HixNoSupportedFiles);
        setIsUploading(false);
        return;
      }

      const result = await apiHixUploadTaskFiles({
        tenantId,
        accountId,
        accessToken,
        companyId: selectedCompanyId!,
        // The generic entry maps to HIX's plain "Upload bestand(en)" draft task: no upload_type_id.
        uploadTypeId: selectedUploadTypeId === GENERIC_UPLOAD_TYPE_KEY ? undefined : selectedUploadTypeId,
        items,
      });

      if (result?.success) {
        if (result.failedFiles && result.failedFiles.length > 0) {
          setErrorMessage(`${languageStrings.HixSomeFilesFailed} ${result.failedFiles.join(", ")}`);
          setIsUploading(false);
        } else {
          const opened = result.taskUrl ? openTaskInBrowser(result.taskUrl) : false;
          if (opened) {
            // The opened task window IS the confirmation — close immediately rather
            // than flashing a success bar nobody sees (focus just moved to the task).
            onDismiss();
          } else {
            // Nothing opened (popup blocked, or HIX returned no recognizable task id):
            // the panel stays open and the persistent success bar is the confirmation,
            // with a direct task link when one exists — a real click cannot be popup-
            // blocked. uploadCompleted prevents submitting the same files again.
            setSuccessMessage(languageStrings.HixUploadSuccess);
            setUploadedTaskUrl(result.taskUrl ?? "");
            setUploadCompleted(true);
            setIsUploading(false);
          }
        }
      } else {
        setErrorMessage(result?.message || languageStrings.HixUploadFailed);
        setIsUploading(false);
      }
    } catch (error) {
      logger(error, "HixUploadPanel.tsx (upload)");
      setErrorMessage(languageStrings.HixUploadFailed);
      setIsUploading(false);
    }
  };

  const onRenderFooterContent = React.useCallback(() => (
    <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="center">
      <PrimaryButton onClick={upload} disabled={!canUpload}>
        {languageStrings.Upload}
      </PrimaryButton>
      <DefaultButton onClick={onDismiss} disabled={isUploading}>
        {languageStrings.Cancel}
      </DefaultButton>
      {isUploading && <Spinner size={SpinnerSize.small} />}
    </Stack>
  ), [canUpload, isUploading, languageStrings, selectedCompanyId, selectedUploadTypeId]);

  return (
    <Panel
      headerText={languageStrings.UploadToHix}
      isOpen={isOpen}
      onDismiss={isUploading ? undefined : onDismiss}
      isLightDismiss={!isUploading}
      onRenderFooterContent={onRenderFooterContent}
      isFooterAtBottom={true}
      type={PanelType.custom}
      customWidth="360px"
      closeButtonAriaLabel={languageStrings.Cancel}
    >
      <Stack tokens={{ childrenGap: 12 }} style={{ marginTop: 12 }}>
        {successMessage && (
          <MessageBar messageBarType={MessageBarType.success}>
            {successMessage}
            {uploadedTaskUrl && (
              <>
                {" "}
                <Link href={uploadedTaskUrl} target="_blank">
                  {languageStrings.HixOpenTask}
                </Link>
              </>
            )}
          </MessageBar>
        )}
        {errorMessage && (
          <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setErrorMessage("")}>
            {errorMessage}
          </MessageBar>
        )}

        {tooManyFiles && (
          <MessageBar messageBarType={MessageBarType.blocked}>
            {languageStrings.HixMaxFilesWarning}
          </MessageBar>
        )}
        {noSupportedFiles && (
          <MessageBar messageBarType={MessageBarType.blocked}>
            {languageStrings.HixNoSupportedFiles}
          </MessageBar>
        )}
        {!noSupportedFiles && unsupportedFiles.length > 0 && (
          <MessageBar messageBarType={MessageBarType.warning}>
            {languageStrings.HixUnsupportedFilesWarning} {unsupportedFiles.map(f => f.name).join(", ")}
          </MessageBar>
        )}

        <ComboBox
          componentRef={comboBoxRef}
          label={languageStrings.HixClient}
          placeholder={languageStrings.HixSearchClient}
          allowFreeform
          autoComplete="on"
          useComboBoxAsMenuWidth
          required
          options={companyOptions}
          selectedKey={selectedCompanyId ?? null}
          onChange={onCompanyChange}
          onInputValueChange={onCompanyInputChange}
          disabled={isUploading}
          iconButtonProps={{
            // While searching, the caret slot hosts the spinner — a conditional spinner
            // row below the field resized the panel content on every search.
            onRenderIcon: isSearching
              ? () => <Spinner size={SpinnerSize.xSmall} ariaLabel={languageStrings.Search} />
              : undefined,
          }}
        />

        <Dropdown
          label={languageStrings.HixUploadType}
          options={uploadTypeDropdownOptions}
          selectedKey={selectedUploadTypeId}
          onChange={onUploadTypeChange}
          disabled={isUploading}
        />

        <Stack tokens={{ childrenGap: 4 }} style={{ marginTop: 8 }}>
          <Text variant="smallPlus" styles={{ root: { fontWeight: 600 } }}>
            {`${languageStrings.HixFilesToUpload} (${supportedFiles.length})`}
          </Text>
          {supportedFiles.map((f, i) => (
            <Text key={`s-${i}`} variant="small">{f.name}</Text>
          ))}
        </Stack>
      </Stack>
    </Panel>
  );
};
